import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Caption,
  Grid,
  Heading,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  SidebarTrigger,
  Stack,
  Text,
  cn,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useCaptureEventOnMount } from '@eluve/posthog-react';

import { CategoryRail, type CategorySelection } from './CategoryRail';
import { OfferingCard } from './OfferingCard';
import {
  type Offering,
  type OfferingType,
  type Org,
  TYPE_META,
  TYPE_ORDER,
  categoryName,
  typeCounts,
} from './care-library-data';
import { useCareLibrary } from './care-library-fhir';
import { TYPE_STYLE } from './offering-types';

type TypeFilter = OfferingType | 'all';

// Tokenized substring match over an offering's searchable fields — cheap to run
// on every keystroke for the catalog sizes we expect, mirroring the care-plans
// list filter. Parameters are display-only and are intentionally not searched.
function matchesSearch(offering: Offering, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return true;
  }
  const hay = [
    offering.name,
    offering.meta,
    offering.desc,
    TYPE_META[offering.type].short,
  ]
    .join(' ')
    .toLowerCase();
  return terms.every((t) => hay.includes(t));
}

const TypeChip: React.FC<{
  label: string;
  count: number;
  on: boolean;
  type?: OfferingType;
  onClick: () => void;
}> = ({ label, count, on, type, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={cn(
      'inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
      on
        ? 'border-bg-strong-primary bg-bg-strong-primary text-text-strong'
        : 'border-border-secondary bg-bg-primary text-text-secondary hover:bg-bg-secondary',
    )}
  >
    {type && (
      <span className={cn('size-1.5 rounded-xs', TYPE_STYLE[type].dot)} />
    )}
    {label}
    <span
      className={cn(
        'font-mono text-2xs',
        on ? 'text-text-strong' : 'text-text-supporting',
      )}
    >
      {count}
    </span>
  </button>
);

const TypeFilterBar: React.FC<{
  org: Org;
  value: TypeFilter;
  onChange: (next: TypeFilter) => void;
}> = ({ org, value, onChange }) => {
  const { t } = useTranslation();
  const counts = typeCounts(org);
  return (
    <Stack
      direction="horizontal"
      gap={1.5}
      align="center"
      wrap
      className="border-b border-border-primary bg-bg-secondary/40 px-6 py-3"
    >
      <Icon name="Filter" size="sm" className="mr-0.5 text-text-tertiary" />
      <TypeChip
        label={t('careLibrary.browse.allTypes')}
        count={org.items.length}
        on={value === 'all'}
        onClick={() => onChange('all')}
      />
      {TYPE_ORDER.filter((t) => counts[t]).map((t) => (
        <TypeChip
          key={t}
          type={t}
          label={TYPE_META[t].short}
          count={counts[t] ?? 0}
          on={value === t}
          onClick={() => onChange(value === t ? 'all' : t)}
        />
      ))}
    </Stack>
  );
};

const pageFrame =
  'col-span-2 row-span-3 flex h-full min-h-0 flex-col overflow-hidden bg-bg-primary';

/** Shared outer frame + minimal header for the loading / error / disabled
 *  states, so the sidebar trigger and page title stay put while the catalog
 *  resolves. */
const CareLibraryShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  return (
    <div className={pageFrame}>
      <Helmet>
        <title>Care Library | Eluve</title>
      </Helmet>
      <Stack
        direction="horizontal"
        gap={3}
        align="center"
        className="border-b border-border-primary px-6 py-3"
      >
        <SidebarTrigger />
        <div className="min-w-0 flex-1">
          <Caption size="3xs" color="tertiary">
            {t('careLibrary.page.eyebrow')}
          </Caption>
          <Heading size="xl" level={1} className="mt-0.5">
            {t('sidebar.careLibrary')}
          </Heading>
        </div>
      </Stack>
      {children}
    </div>
  );
};

const CenteredNotice: React.FC<{
  icon: 'Layers' | 'AlertTriangle';
  text: string;
}> = ({ icon, text }) => (
  <Stack gap={2} align="center" className="px-6 py-24 text-center">
    <Icon name={icon} size="xl" className="text-text-disabled" />
    <Text color="tertiary">{text}</Text>
  </Stack>
);

export const CareLibraryPage: React.FC = () => {
  useCaptureEventOnMount('page_view_care_library');
  const { t } = useTranslation();
  const { org, isError, enabled } = useCareLibrary();

  if (isError) {
    return (
      <CareLibraryShell>
        <CenteredNotice
          icon="AlertTriangle"
          text={t('careLibrary.page.loadError')}
        />
      </CareLibraryShell>
    );
  }
  if (!enabled && !org) {
    return (
      <CareLibraryShell>
        <CenteredNotice
          icon="Layers"
          text={t('careLibrary.page.medplumRequired')}
        />
      </CareLibraryShell>
    );
  }
  if (!org) {
    return (
      <CareLibraryShell>
        <CenteredNotice
          icon="Layers"
          text={t('careLibrary.page.loadingOfferings')}
        />
      </CareLibraryShell>
    );
  }

  return <CatalogBrowse org={org} />;
};

const CatalogBrowse: React.FC<{ org: Org }> = ({ org }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth for the active filters, so the browse view
  // is deep-linkable, e.g. ?type=exercise&facet=region&cat=lumbar&q=press.
  const search = searchParams.get('q') ?? '';
  const rawType = searchParams.get('type');
  const typeFilter: TypeFilter =
    rawType && TYPE_ORDER.includes(rawType as OfferingType)
      ? (rawType as OfferingType)
      : 'all';
  const facetId = searchParams.get('facet');
  const catId = searchParams.get('cat');
  const category: CategorySelection | null =
    facetId &&
    catId &&
    org.facets.some(
      (f) => f.id === facetId && f.cats.some((c) => c.id === catId),
    )
      ? { facetId, catId }
      : null;

  // Merge into the existing params and replace history so back/forward isn't
  // polluted by every keystroke or chip toggle.
  const patchParams = (mutate: (params: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );

  const setSearch = (value: string) =>
    patchParams((p) => (value ? p.set('q', value) : p.delete('q')));

  const setTypeFilter = (next: TypeFilter) =>
    patchParams((p) =>
      next === 'all' ? p.delete('type') : p.set('type', next),
    );

  const setCategory = (selection: CategorySelection | null) =>
    patchParams((p) => {
      if (selection) {
        p.set('facet', selection.facetId);
        p.set('cat', selection.catId);
      } else {
        p.delete('facet');
        p.delete('cat');
      }
    });

  const filtered = org.items.filter(
    (it) =>
      (typeFilter === 'all' || it.type === typeFilter) &&
      (category === null ||
        (it.cats[category.facetId] ?? []).includes(category.catId)) &&
      matchesSearch(it, search),
  );

  const resultLabel = category
    ? categoryName(org, category.facetId, category.catId)
    : typeFilter !== 'all'
      ? t('careLibrary.browse.typeOfferings', {
          type: TYPE_META[typeFilter].short,
        })
      : t('careLibrary.browse.allOfferings');

  const typeCount = Object.keys(typeCounts(org)).length;

  return (
    <div className={pageFrame}>
      <Helmet>
        <title>Care Library | Eluve</title>
      </Helmet>

      {/* Topbar — title block + search + author/new actions */}
      <Stack
        direction="horizontal"
        gap={3}
        align="center"
        className="border-b border-border-primary px-6 py-3"
      >
        <SidebarTrigger />
        <div className="min-w-0 flex-1">
          <Caption size="3xs" color="tertiary">
            {t('careLibrary.page.eyebrow')}
          </Caption>
          <Heading size="xl" level={1} className="mt-0.5">
            {t('sidebar.careLibrary')}
          </Heading>
          <Text size="xs" color="tertiary" className="mt-1">
            <span className="font-mono">{org.items.length}</span>{' '}
            {t('careLibrary.browse.offeringsCountLabel')} ·{' '}
            <span className="font-mono">{typeCount}</span>{' '}
            {t('careLibrary.browse.typesCountLabel')} · {org.name}
          </Text>
        </div>
        <InputGroup className="w-56 shrink-0">
          <InputGroupAddon align="inline-start">
            <Icon name="Search" size="sm" />
          </InputGroupAddon>
          <InputGroupInput
            placeholder={t('careLibrary.browse.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('careLibrary.browse.searchAriaLabel')}
          />
        </InputGroup>
      </Stack>

      <TypeFilterBar org={org} value={typeFilter} onChange={setTypeFilter} />

      {/* Body — left category tree (Option A) + results grid */}
      <div className="grid min-h-0 flex-1 grid-cols-[216px_1fr]">
        <div className="min-h-0 overflow-y-auto">
          <CategoryRail org={org} selected={category} onSelect={setCategory} />
        </div>

        <div className="flex min-h-0 flex-col overflow-y-auto">
          <Stack
            direction="horizontal"
            gap={2}
            align="center"
            className="px-6 pt-4 pb-2"
          >
            <Text inline size="sm" weight="semibold">
              {resultLabel}
            </Text>
            <Text inline size="xs" color="tertiary" className="font-mono">
              {filtered.length}
            </Text>
          </Stack>

          {filtered.length === 0 ? (
            <Stack gap={2} align="center" className="px-6 py-20 text-center">
              <Icon name="Layers" size="xl" className="text-text-disabled" />
              <Text color="tertiary">{t('careLibrary.browse.noMatches')}</Text>
            </Stack>
          ) : (
            <Grid columns={3} gap={3} className="px-6 pb-6">
              {filtered.map((offering) => (
                <OfferingCard
                  key={offering.id}
                  offering={offering}
                  org={org}
                  onClick={() =>
                    navigate(
                      `/organizations/${tenantId}/care-library/${offering.id}`,
                    )
                  }
                />
              ))}
            </Grid>
          )}
        </div>
      </div>
    </div>
  );
};
